import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { MoreHorizontal, Pencil, Trash2, Plus, ChevronRight, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSaveTemplate } from "@/hooks/useTemplates";
import { usePromptLibraries, useTemplateAssociations } from "@/hooks/usePromptLibrary";
import { useAdminTemplateTypes, useAdminTemplateFormatTypes } from "@/hooks/useMasterData";
import { promptLibraryApi } from "@/services/promptLibraryApi";

export interface TemplateField {
  /** Prompt library ID — present for existing columns in edit mode */
  id?: string;
  label: string;
  question: string;
  ordinal?: number;
}

interface TemplateFormValues {
  name: string;
  description: string;
  is_active: boolean;
  template_type_id: number | null;
  template_formatetype_ids: number[];
  fields: TemplateField[];
}

interface ValidationRule {
  rule: string;
  value: string;
  message?: string;
  /** Format type name — rule only applies when this format type is selected */
  format_type?: string;
}

interface DialogFormValues {
  label: string;
  question: string;
  ordinal: number;
}

interface TemplateFormProps {
  onClose: () => void;
  onExtract?: (fields: TemplateField[]) => void;
  /** Provide when editing an existing template. Omit for create mode. */
  defaultValues?: { id?: string; name?: string; description?: string; is_active?: boolean; template_type_id?: number | null; template_formatetype_ids?: number[] | null };
  /** When provided, called on submit instead of useSaveTemplate (edit mode). */
  onSave?: (data: { name: string; description: string; is_active: boolean; template_type_id: number | null; template_formatetype_ids: number[] }) => Promise<void>;
}

const DEFAULT_FIELDS: TemplateField[] = [
  // { label: "Population",   question: "What was the study population?" },
  // { label: "Intervention", question: "What was the intervention?" },
  // { label: "Comparator",   question: "What was the comparator?" },
  // { label: "Outcomes",     question: "What was the outcomes?" },
];

const TemplateForm = ({ onClose, onExtract, defaultValues, onSave }: TemplateFormProps) => {
  const templateId = defaultValues?.id;
  const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);
  const [editDialogIndex, setEditDialogIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    newTypeId: number | null;
    newFormatIds: number[];
    onChange: (v: unknown) => void;
    conflictNames: string[];
  } | null>(null);

  const saveTemplate = useSaveTemplate();
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogSaving, setIsDialogSaving] = useState(false);
  const [formatTypesOpen, setFormatTypesOpen] = useState(false);
  const { data: allTemplateTypes = [] } = useAdminTemplateTypes();
  const { data: allFormatTypes = [] } = useAdminTemplateFormatTypes();
  const activeTemplateTypes = allTemplateTypes.filter((t) => t.is_active);
  const activeFormatTypes = allFormatTypes.filter((t) => t.is_active);
  const { data: promptLibraries = [], isLoading: librariesLoading, isError: librariesError } = usePromptLibraries();
  const { data: associations = [] } = useTemplateAssociations(templateId);

  // Main form
  const { control, handleSubmit, reset, getValues, setValue, register, watch } = useForm<TemplateFormValues>({
    defaultValues: {
      name: defaultValues?.name ?? "",
      description: defaultValues?.description ?? "",
      is_active: defaultValues?.is_active ?? true,
      template_type_id: defaultValues?.template_type_id ?? null,
      template_formatetype_ids: defaultValues?.template_formatetype_ids ?? [],
      fields: DEFAULT_FIELDS,
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: "fields", keyName: "_id" });
  const watchedFields = useWatch({ control, name: "fields" }) as TemplateField[];
  const watchedTemplateTypeId = watch("template_type_id");
  const watchedFormatTypeIds = watch("template_formatetype_ids");

  const activeTemplateType = useMemo(
    () => allTemplateTypes.find((t) => t.id === watchedTemplateTypeId) ?? null,
    [allTemplateTypes, watchedTemplateTypeId]
  );

  const validationRules = useMemo<ValidationRule[]>(() => {
    if (!activeTemplateType?.settings) return [];
    try {
      const parsed = JSON.parse(activeTemplateType.settings);
      return Array.isArray(parsed?.validation) ? parsed.validation : [];
    } catch {
      return [];
    }
  }, [activeTemplateType]);

  const validationErrors = useMemo<string[]>(() => {
    if (validationRules.length === 0) return [];
    const sorted = [...watchedFields].sort(
      (a, b) => (a.ordinal ?? Infinity) - (b.ordinal ?? Infinity)
    );
    const errors: string[] = [];
    for (const rule of validationRules) {
      if (rule.format_type) {
        const matchingFt = allFormatTypes.find(
          (ft) => ft.name.toLowerCase() === rule.format_type!.toLowerCase()
        );
        if (!matchingFt || !watchedFormatTypeIds.includes(matchingFt.id)) continue;
      }
      if (rule.rule === "first_column_name") {
        const first = sorted[0];
        if (!first || !first.label.trim().toLowerCase().startsWith(rule.value.toLowerCase())) {
          errors.push(rule.message ?? `First column must start with "${rule.value}"`);
        }
      }
    }
    return errors;
  }, [watchedFields, validationRules, allFormatTypes, watchedFormatTypeIds]);

  const getConflictingIndices = (newTypeId: number | null, newFormatIds: number[]) => {
    return getValues("fields")
      .map((field, i) => ({ field, i }))
      .filter(({ field }) => {
        if (!field.id) return false;
        const pl = promptLibraries.find((p) => p.id === field.id);
        if (!pl) return false;
        const typeOk = newTypeId == null || pl.template_type_id === newTypeId;
        const formatOk = newFormatIds.length === 0 ||
          pl.template_formatetype_ids?.some((id) => newFormatIds.includes(id));
        return !(typeOk && formatOk);
      })
      .map(({ i }) => i);
  };

  const groupedLibraries = useMemo(() => {
    const groups: Record<string, typeof promptLibraries> = {};
    for (const pl of promptLibraries) {
      if (!pl.is_active) continue;
      if (watchedTemplateTypeId != null && pl.template_type_id !== watchedTemplateTypeId) continue;
      if (watchedFormatTypeIds.length > 0 &&
          !pl.template_formatetype_ids?.some((id) => watchedFormatTypeIds.includes(id))) continue;
      if (!groups[pl.name]) groups[pl.name] = [];
      groups[pl.name].push(pl);
    }
    return groups;
  }, [promptLibraries, watchedTemplateTypeId, watchedFormatTypeIds]);

  const sortedIndices = [...Array(fields.length).keys()].sort(
    (a, b) => ((watchedFields[a]?.ordinal ?? Infinity) - (watchedFields[b]?.ordinal ?? Infinity))
  );

  // Edit dialog local form
  const dialogForm = useForm<DialogFormValues>({ defaultValues: { label: "", question: "", ordinal: 0 } });

  // In edit mode: populate the fields array from the template's existing prompt libraries.
  // Use a ref guard so background refetches don't reset the user's in-progress reordering.
  const fieldsInitializedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!templateId || !associations.length || !promptLibraries.length) return;
    if (fieldsInitializedForRef.current === templateId) return;

    fieldsInitializedForRef.current = templateId;

    const existingFields: TemplateField[] = associations
      .flatMap((a) => {
        const pl = promptLibraries.find((p) => p.id === a.prompt_library_id);
        return pl ? [{ id: pl.id, label: pl.name, question: pl.priming ?? "", ordinal: a.ordinal ?? undefined }] : [];
      })
      .sort((a, b) => ((a.ordinal ?? Infinity) - (b.ordinal ?? Infinity)))
      .map((f, i) => ({ ...f, ordinal: f.ordinal ?? i }));

    reset((prev) => ({ ...prev, fields: existingFields }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, associations.length, promptLibraries.length]);

  // Sync dialog form when the target field changes
  useEffect(() => {
    if (editDialogIndex !== null) {
      const current = getValues(`fields.${editDialogIndex}`);
      dialogForm.reset({ label: current?.label ?? "", question: current?.question ?? "", ordinal: current?.ordinal ?? 0 });
    }
  }, [editDialogIndex, dialogForm, getValues]);

  const openEditDialog = (index: number) => {
    setOpenPopoverIndex(null);
    setEditDialogIndex(index);
  };

  const saveEditDialog = dialogForm.handleSubmit(async (data) => {
    if (editDialogIndex !== null) {
      const currentField = getValues(`fields.${editDialogIndex}`);
      setValue(`fields.${editDialogIndex}.label`, data.label);
      setValue(`fields.${editDialogIndex}.question`, data.question);
      setValue(`fields.${editDialogIndex}.ordinal`, data.ordinal);

      if (currentField?.id && templateId) {
        setIsDialogSaving(true);
        try {
          // Update prompt library name and priming
          await promptLibraryApi.updatePromptLibrary(currentField.id, {
            name: data.label,
            priming: data.question,
          });
          // Update association ordinal
          const association = associations.find((a) => a.prompt_library_id === currentField.id);
          if (association) {
            await promptLibraryApi.updateAssociation(association.id, { ordinal: data.ordinal });
          }
          qc.invalidateQueries({ queryKey: ["prompt-libraries"] });
          qc.invalidateQueries({ queryKey: ["template-associations"] });
        } finally {
          setIsDialogSaving(false);
        }
      }
    }
    setEditDialogIndex(null);
  });

  const handleDeleteRequest = (index: number) => {
    setOpenPopoverIndex(null);
    setDeleteIndex(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) remove(deleteIndex);
    setDeleteIndex(null);
  };

  const nextOrdinal = () => {
    const current = getValues("fields");
    return current.reduce((max, f) => Math.max(max, f.ordinal ?? 0), -1) + 1;
  };

  const handleSelectPromptLibrary = (id: string, name: string, priming: string) => {
    append({ id, label: name, question: priming, ordinal: nextOrdinal() });
    setAddColumnOpen(false);
  };

  const handleCreateNewColumn = () => {
    append({ label: "", question: "", ordinal: nextOrdinal() });
    setEditDialogIndex(fields.length);
    setAddColumnOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: TemplateFormValues) => {
    // Re-derive validation at submit time from data (never stale, even if types loaded after render)
    const submitType = allTemplateTypes.find((t) => t.id === data.template_type_id) ?? null;
    if (submitType?.settings) {
      try {
        const parsed = JSON.parse(submitType.settings);
        const rules: ValidationRule[] = Array.isArray(parsed?.validation) ? parsed.validation : [];
        const sorted = [...data.fields].sort((a, b) => (a.ordinal ?? Infinity) - (b.ordinal ?? Infinity));
        for (const rule of rules) {
          if (rule.format_type) {
            const matchingFt = allFormatTypes.find(
              (ft) => ft.name.toLowerCase() === rule.format_type!.toLowerCase()
            );
            if (!matchingFt || !data.template_formatetype_ids.includes(matchingFt.id)) continue;
          }
          if (rule.rule === "first_column_name") {
            const first = sorted[0];
            if (!first || !first.label.trim().toLowerCase().startsWith(rule.value.toLowerCase())) return;
          }
        }
      } catch { /* ignore parse errors */ }
    }
    if (onSave) {
      setIsSaving(true);
      try {
        await onSave({ name: data.name, description: data.description, is_active: data.is_active, template_type_id: data.template_type_id, template_formatetype_ids: data.template_formatetype_ids });

        const existingAssociatedIds = new Set(associations.map((a) => a.prompt_library_id));
        const currentFieldIds = new Set(data.fields.filter((f) => f.id).map((f) => f.id!));

        await Promise.all([
          // Delete associations for removed fields
          ...associations
            .filter((a) => !currentFieldIds.has(a.prompt_library_id))
            .map((a) => promptLibraryApi.deleteAssociation(a.id)),
          // Process current fields
          ...data.fields.map(async (f) => {
            if (!f.id) {
              // Brand new column: create prompt library then association (must be sequential)
              const plId = crypto.randomUUID();
              await promptLibraryApi.createPromptLibrary({ id: plId, name: f.label, priming: f.question });
              await promptLibraryApi.createAssociation({ id: crypto.randomUUID(), template_id: templateId!, prompt_library_id: plId, ordinal: f.ordinal });
            } else if (!existingAssociatedIds.has(f.id)) {
              // Existing prompt library newly added: create association only
              await promptLibraryApi.createAssociation({ id: crypto.randomUUID(), template_id: templateId!, prompt_library_id: f.id, ordinal: f.ordinal });
            }
            // Already associated existing columns: label/priming/ordinal saved via Edit dialog
          }),
        ]);

        qc.invalidateQueries({ queryKey: ["templates"] });
        qc.invalidateQueries({ queryKey: ["prompt-libraries"] });
        qc.invalidateQueries({ queryKey: ["template-associations"] });
      } finally {
        setIsSaving(false);
      }
    } else {
      await saveTemplate.mutateAsync({ name: data.name, description: data.description, fields: data.fields, template_type_id: data.template_type_id, template_formatetype_ids: data.template_formatetype_ids });
      onExtract?.(data.fields);
    }
    onClose();
  };

  const isPending = onSave ? isSaving : saveTemplate.isPending;
  const popoverItemClass = "w-full text-left text-sm px-3 py-1.5 rounded transition-colors flex items-center gap-2";

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
        {/* Template metadata */}
        <div className="space-y-4 pb-6 border-b">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Template name</label>
            <Controller
              control={control}
              name="name"
              rules={{ required: true }}
              render={({ field: f, fieldState }) => (
                <Input {...f} placeholder="e.g. JCA-PICO" className={fieldState.error ? "border-destructive" : ""} />
              )}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Description</label>
            <Controller
              control={control}
              name="description"
              render={({ field: f }) => (
                <Textarea {...f} placeholder="Describe this template..." className="min-h-[60px]" />
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <Controller
              control={control}
              name="is_active"
              render={({ field: f }) => (
                <Switch
                  id="is-active"
                  checked={f.value}
                  onCheckedChange={f.onChange}
                />
              )}
            />
            <Label htmlFor="is-active" className="cursor-pointer text-sm font-medium">Active</Label>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Template Type <span className="text-destructive">*</span></label>
            <Controller
              control={control}
              name="template_type_id"
              rules={{ required: true }}
              render={({ field: f, fieldState }) => (
                <Select
                  value={f.value != null ? String(f.value) : ""}
                  onValueChange={(v) => {
                    const newId = Number(v);
                    const newFormatIds = getValues("template_formatetype_ids");
                    const conflictIndices = getConflictingIndices(newId, newFormatIds);
                    if (conflictIndices.length > 0) {
                      setPendingChange({
                        newTypeId: newId,
                        newFormatIds,
                        onChange: f.onChange,
                        conflictNames: conflictIndices.map((i) => getValues(`fields.${i}.label`) || "Untitled"),
                      });
                    } else {
                      f.onChange(newId);
                    }
                  }}
                >
                  <SelectTrigger className={fieldState.error ? "border-destructive" : ""}>
                    <SelectValue placeholder="Select template type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeTemplateTypes.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Format Types <span className="text-destructive">*</span></label>
            <Controller
              control={control}
              name="template_formatetype_ids"
              rules={{ validate: (v) => v.length > 0 }}
              render={({ field: f, fieldState }) => (
                <div className="space-y-2">
                  <Popover open={formatTypesOpen} onOpenChange={setFormatTypesOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className={`w-full justify-start font-normal ${fieldState.error ? "border-destructive" : ""}`}>
                        {f.value.length > 0 ? `${f.value.length} selected` : "Select format types..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-1" align="start">
                      {activeFormatTypes.length === 0 && (
                        <p className="text-xs text-muted-foreground px-2 py-1.5">No format types available.</p>
                      )}
                      {activeFormatTypes.map((ft) => {
                        const checked = f.value.includes(ft.id);
                        return (
                          <div
                            key={ft.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer"
                            onClick={() => {
                              const newIds = checked ? f.value.filter((id) => id !== ft.id) : [...f.value, ft.id];
                              const currentTypeId = getValues("template_type_id");
                              const conflictIndices = getConflictingIndices(currentTypeId, newIds);
                              if (conflictIndices.length > 0) {
                                setPendingChange({
                                  newTypeId: currentTypeId,
                                  newFormatIds: newIds,
                                  onChange: f.onChange,
                                  conflictNames: conflictIndices.map((i) => getValues(`fields.${i}.label`) || "Untitled"),
                                });
                              } else {
                                f.onChange(newIds);
                              }
                            }}
                          >
                            <Checkbox checked={checked} onCheckedChange={() => {}} />
                            <span className="text-sm">{ft.name}</span>
                          </div>
                        );
                      })}
                    </PopoverContent>
                  </Popover>
                  {f.value.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {f.value.map((id) => {
                        const ft = activeFormatTypes.find((t) => t.id === id);
                        return ft ? (
                          <Badge key={id} variant="secondary" className="gap-1">
                            {ft.name}
                            <button
                              type="button"
                              onClick={() => {
                                const newIds = f.value.filter((v) => v !== id);
                                const currentTypeId = getValues("template_type_id");
                                const conflictIndices = getConflictingIndices(currentTypeId, newIds);
                                if (conflictIndices.length > 0) {
                                  setPendingChange({
                                    newTypeId: currentTypeId,
                                    newFormatIds: newIds,
                                    onChange: f.onChange,
                                    conflictNames: conflictIndices.map((i) => getValues(`fields.${i}.label`) || "Untitled"),
                                  });
                                } else {
                                  f.onChange(newIds);
                                }
                              }}
                              className="ml-0.5 rounded-full hover:bg-muted-foreground/20"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                  {fieldState.error && <p className="text-xs text-destructive">At least one format type is required.</p>}
                </div>
              )}
            />
          </div>
        </div>

        {/* Columns list */}
        <h3 className="font-semibold">Columns</h3>

        {sortedIndices.map((index) => {
          const field = fields[index];
          return (<div key={field._id}>
            <input type="hidden" {...register(`fields.${index}.id`)} />
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name={`fields.${index}.ordinal`}
                  render={({ field: f }) => (
                    <span 
                    className="inline-block p-1 text-center bg-gray-100 text-xs text-muted-foreground w-6">
                      {f.value ?? "—"}
                    </span>
                  )}
                />
                <Controller
                  control={control}
                  name={`fields.${index}.label`}
                  render={({ field: f }) => (
                    <span className="text-sm font-medium">{f.value || "Untitled"}</span>
                  )}
                />
              </div>
              <Popover
                open={openPopoverIndex === index}
                onOpenChange={(open) => setOpenPopoverIndex(open ? index : null)}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-brand-teal transition-colors ml-2 p-1 rounded"
                    aria-label="Field options"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-36 p-1" align="end">
                  <button type="button" className={`${popoverItemClass} hover:bg-accent`} onClick={() => openEditDialog(index)}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button type="button" className={`${popoverItemClass} text-destructive hover:bg-destructive/10`} onClick={() => handleDeleteRequest(index)}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </PopoverContent>
              </Popover>
            </div>
            <Controller
              control={control}
              name={`fields.${index}.question`}
              render={({ field: f }) => (
                <Textarea
                  {...f}
                  placeholder="What was the question?"
                  className="min-h-[60px] focus-visible:ring-0 focus-visible:ring-offset-0 cursor-default"
                  readOnly
                />
              )}
            />
          </div>
          );
        })}

        {validationErrors.length > 0 && (
          <div className="space-y-1">
            {validationErrors.map((msg, i) => (
              <p key={i} className="text-sm text-destructive">{msg}</p>
            ))}
          </div>
        )}

        {/* Add column */}
        <Popover open={addColumnOpen} onOpenChange={(open) => { setAddColumnOpen(open); if (!open) setActiveGroup(null); }}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className="w-full border-dashed" disabled={!watchedTemplateTypeId || watchedFormatTypeIds.length === 0}>
              + Add column
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search columns..." />
              <CommandList>
                {librariesLoading && (
                  <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>
                )}
                {librariesError && (
                  <div className="py-6 text-center text-sm text-destructive">Failed to load columns.</div>
                )}
                {!librariesLoading && !librariesError && (
                  <>
                    <CommandEmpty>No columns found.</CommandEmpty>
                    {Object.keys(groupedLibraries).length > 0 && (
                      <CommandGroup heading="Existing columns">
                        {Object.entries(groupedLibraries).map(([groupName, items]) => (
                          <Popover
                            key={groupName}
                            open={activeGroup === groupName}
                            onOpenChange={(open) => setActiveGroup(open ? groupName : null)}
                          >
                            <PopoverTrigger asChild>
                              <CommandItem
                                value={groupName}
                                className="flex items-center justify-between cursor-pointer"
                                onSelect={() => setActiveGroup(activeGroup === groupName ? null : groupName)}
                              >
                                <span>{groupName}</span>
                                <ChevronRight className="w-4 h-4 shrink-0" />
                              </CommandItem>
                            </PopoverTrigger>
                            <PopoverContent side="right" align="start" className="min-w-[240px] p-1">
                              {items.map((pl) => (
                                <div
                                  key={pl.id}
                                  className="flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                                  onClick={() => handleSelectPromptLibrary(pl.id, pl.name, pl.priming ?? "")}
                                >
                                  <span className="truncate">{pl.priming || pl.name}</span>
                                </div>
                              ))}
                            </PopoverContent>
                          </Popover>
                        ))}
                      </CommandGroup>
                    )}
                    <CommandSeparator />
                  </>
                )}
                <CommandGroup>
                  <CommandItem onSelect={handleCreateNewColumn}>
                    <Plus className="w-4 h-4 mr-2" /> Create new column
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Form actions */}
        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" className="flex-1 bg-brand-teal text-white hover:bg-brand-teal/90" disabled={isPending || validationErrors.length > 0}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>

      {/* Edit column dialog */}
      <Dialog open={editDialogIndex !== null} onOpenChange={(open) => { if (!open) setEditDialogIndex(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit column</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Label</label>
              <Controller
                control={dialogForm.control}
                name="label"
                rules={{ required: true }}
                render={({ field: f, fieldState }) => (
                  <Input {...f} placeholder="Column name" className={fieldState.error ? "border-destructive" : ""} />
                )}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Question</label>
              <Controller
                control={dialogForm.control}
                name="question"
                render={({ field: f }) => (
                  <Textarea {...f} placeholder="What question should be asked?" className="min-h-[80px]" />
                )}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Order</label>
              <Controller
                control={dialogForm.control}
                name="ordinal"
                render={({ field: f }) => (
                  <Input
                    {...f}
                    type="number"
                    placeholder="0"
                    onChange={(e) => f.onChange(e.target.valueAsNumber)}
                  />
                )}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditDialogIndex(null)} disabled={isDialogSaving}>Cancel</Button>
            <Button type="button" className="bg-brand-teal text-white hover:bg-brand-teal/90" onClick={saveEditDialog} disabled={isDialogSaving}>
              {isDialogSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type/format conflict dialog */}
      <AlertDialog open={!!pendingChange} onOpenChange={(open) => { if (!open) setPendingChange(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove non-matching columns?</AlertDialogTitle>
            <AlertDialogDescription>
              The following columns don't match the new type or format and will be removed:{" "}
              <strong>{pendingChange?.conflictNames.join(", ")}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingChange(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingChange) return;
                const indices = getConflictingIndices(pendingChange.newTypeId, pendingChange.newFormatIds);
                [...indices].sort((a, b) => b - a).forEach((i) => remove(i));
                const currentTypeId = getValues("template_type_id");
                pendingChange.onChange(
                  pendingChange.newTypeId !== currentTypeId
                    ? pendingChange.newTypeId
                    : pendingChange.newFormatIds
                );
                setPendingChange(null);
              }}
            >
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm dialog */}
      <AlertDialog open={deleteIndex !== null} onOpenChange={(open) => { if (!open) setDeleteIndex(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete column</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete this column? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TemplateForm;
